/* Formatting and the small pieces of arithmetic that more than one screen
 * needs. Kept here so the queue and the workspace cannot disagree about what
 * "3:02" or "₹18.00" means. */

import type { Subtask, Task, VideoRow, Hands } from "../api/types";
import { countMarks } from "./verdict";

/** mm:ss, or h:mm:ss past an hour.
 *
 *  MINUTES ARE ALWAYS TWO DIGITS. It used to be m:ss under an hour, so a
 *  30-second clip read "0:30" beside a 3-minute one at "3:00" and the columns
 *  did not line up. Two digits keeps every duration the same width, which is
 *  what makes a column of them scannable. */
export function clock(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) seconds = 0;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h ? `${h}:` : ""}${String(m).padStart(2, "0")}:`
    + String(s).padStart(2, "0");
}

/** mm:ss.mmm — for boundaries, where a millisecond is the whole point.
 *
 *  THE FRACTION IS NEVER RECOVERED BY SUBTRACTION. `seconds - Math.floor(...)`
 *  is where binary floating point shows through: 3.8 - 3 is 0.7999999999999998.
 *  Rounding hid it here, but rounding introduced its own edge — 3.9995 gave
 *  frac 1000 and rendered "00:03.1000". Converting to whole milliseconds FIRST
 *  makes the carry ordinary arithmetic: 3.9995 becomes 4000ms, i.e. 00:04.000. */
export function preciseClock(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) seconds = 0;
  const ms = Math.round(seconds * 1000);
  return `${clock(Math.floor(ms / 1000))}.${String(ms % 1000).padStart(3, "0")}`;
}

/** mm:ss.t — a sub-task's LENGTH, to a tenth.
 *
 *  The timeline printed these as "3.8s" while every other time on the screen
 *  was a clock, so one lane disagreed with the ruler above it and with the
 *  fields beside it. A tenth is the resolution the boundary controls move in
 *  (±⅒s), so it is the right precision here — milliseconds belong on a
 *  boundary, where preciseClock is used. */
export function clockTenths(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) seconds = 0;
  // In tenths, for the reason preciseClock explains. Truncating the naive
  // subtraction rendered a 3.8s block as 00:03.7 — caught by its own test.
  // The epsilon absorbs the representation error without reaching the next
  // tenth: 3.99 stays 00:03.9, which is what truncation must mean here, or two
  // adjacent blocks would appear to overlap.
  const tenths = Math.floor(seconds * 10 + 1e-6);
  return `${clock(Math.floor(tenths / 10))}.${tenths % 10}`;
}

/** A video's length. ONE FORMAT, whatever the length.
 *
 *  Anything under a minute used to render as "30.0s" while everything else was
 *  a clock, so the Length column mixed "30.0s" and "3:00" — two notations for
 *  one quantity, and the short one carried a false tenth of a second that no
 *  other row showed. It is `clock` for every duration now: 00:30, 03:00, 42:10. */
export function duration(seconds: number | null | undefined): string {
  if (!seconds) return "—";
  return clock(seconds);
}

export function money(amount: number, currency = "INR"): string {
  const symbol = currency === "INR" ? "₹" : currency === "USD" ? "$" : `${currency} `;
  return `${symbol}${amount.toFixed(2)}`;
}

/** The file name, which is what a person recognises. The full s3 key is shown
 *  separately on the workspace header for anyone who needs to find the object. */
export function videoName(path: string): string {
  const last = path.split("/").pop() ?? path;
  return last.replace(/\.[^.]+$/, "");
}

/** Does this row match what was typed in the search box?
 *
 *  IT SEARCHES WHAT THE ROW SHOWS. It used to look at the file name and the
 *  session code only, which produced a result that looked like a filter bug and
 *  was worse than one: typing "Manufacturing" returned the four Skilled Trades ·
 *  Frames MANUFACTURING videos and none of the Manufacturing ones, because
 *  those are named mfg_assembling_001. The word was on screen, in the Category
 *  column of rows that were then hidden.
 *
 *  So the haystack is every field the row displays. Anything visible is
 *  searchable, and nothing invisible is — a user cannot reason about a filter
 *  that reads fields they cannot see.
 *
 *  Category and sub-category are matched both separately and as the joined
 *  "Manufacturing · Coiling" string, so a query that spans the separator still
 *  works and the chip labels are searchable as written. */
export function matchesQuery(row: VideoRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    videoName(row.video_path),
    row.session_code,
    row.category,
    row.sub_category,
    [row.category, row.sub_category].filter(Boolean).join(" · "),
  ];
  return haystack.some((f) => (f ?? "").toLowerCase().includes(q));
}

export const HAND_LABEL: Record<Hands, string> = {
  left: "Left hand",
  right: "Right hand",
  both: "Both hands",
  idle: "Idle",
};

/** The hand icons, COPIED FROM THE PROTOTYPE — `HANDICON` in
 *  docs/Video_Annotation_Tool_v1.6.html, value for value:
 *
 *      HANDICON = {left:'🫲', right:'🫱', both:'🙌', none:'·'};
 *
 *  🫲 U+1FAF2 LEFTWARDS HAND and 🫱 U+1FAF1 RIGHTWARDS HAND are genuine hands,
 *  mirrored — so the glyph itself says which side, which is the entire reason
 *  the control has icons. They are Unicode 14 (2021).
 *
 *  I twice guessed at this instead of reading it: first 👈 / 👉 (`backhand index
 *  pointing`, a "look over there" gesture, not a hand), then the same ✋ for
 *  both sides, which cannot distinguish them at all. I had ruled the Unicode 14
 *  pair out as "still tofu on plenty of systems" — a guess, and wrong: the deck
 *  ships them and they render. The lesson is cheap and worth keeping — the
 *  prototype is IN THE REPO; grep it rather than infer it from a screenshot.
 *
 *  ONE MAP, TWO PLACES: the inspector's segmented control and the timeline
 *  blocks. Changing it here changes both, which is the point — hands must not
 *  have one vocabulary in the panel and another in the lane below it.
 *
 *  Idle is a middle dot, as in the deck: no hands in use should be quiet rather
 *  than a third symbol competing with the two that mean something.
 *
 *  Render these with `font-emoji` (see index.css). It matters more here than it
 *  would for older emoji: a font predating 2021 has the other glyphs and not
 *  these, so the named colour-emoji families are what keep them from falling
 *  back to tofu. */
export const HAND_GLYPH: Record<Hands, string> = {
  left: "🫲", right: "🫱", both: "🙌", idle: "·",
};

/** The segmented control's four buttons (4.2.5). A separate map, not
 *  `HAND_LABEL.replace(" hand", "")` — that produced "Boths", because the
 *  singular pattern matches inside "Both hands" and leaves the trailing s. */
export const HAND_SHORT: Record<Hands, string> = {
  left: "Left", right: "Right", both: "Both", idle: "Idle",
};

/** 4.2.1: the automatic title is "verb · object", with a repeat counter when
 *  the same verb+object occurs more than once in the task. The counter is
 *  computed from position, not stored — 8.2.1, and a stored one would go stale
 *  the moment a sub-task is deleted. */
export function subtaskTitle(sub: Subtask, siblings: Subtask[]): string {
  if (sub.custom_title) return sub.custom_title;
  const parts = [sub.verb, sub.object_name].filter(Boolean);

  // A "no action" event has no verb and no object BY DESIGN — the analyzer
  // emits hands:null for a stretch where the hands do nothing, and the bridge
  // maps that to hands:"idle" (1.3.4). Those are LABELLED, as idle. Calling
  // them "(unlabelled)" told the annotator the model had failed on 9 of the
  // 125 sub-tasks in the brick-work video when it had in fact answered
  // correctly. "(unlabelled)" is kept for the genuinely empty case.
  if (!parts.length) return sub.hands === "idle" ? "No action" : "(unlabelled)";

  const base = parts.join(" · ");

  const key = `${sub.verb ?? ""}|${sub.object_name ?? ""}`;
  const same = siblings.filter(
    (s) => `${s.verb ?? ""}|${s.object_name ?? ""}` === key,
  );
  if (same.length < 2) return base;
  const k = same.findIndex((s) => s.id === sub.id) + 1;
  return `${base} #${k}`;
}

export const isResolved = (s: Subtask) =>
  s.status === "APPROVED" ||
  (s.status === "REJECTED" && !!s.rejection_reason?.trim());

export function counts(tasks: Task[]) {
  const all = tasks.flatMap((t) => t.subtasks);
  return {
    total: all.length,
    approved: all.filter((s) => s.status === "APPROVED").length,
    rejected: all.filter((s) => s.status === "REJECTED").length,
    pending: all.filter((s) => s.status === "PENDING").length,
    // TWO NUMBERS, NOT ONE — see lib/verdict.ts. `flagged` is what the queue
    // card counts, so the two screens state the same workload; `corrected` is
    // "the AI rewrote this", shown but never counted as work to do. This was
    // one field testing `decision !== "AUTO_ACCEPT"`, which put 75 on a video
    // the queue called 10.
    ...countMarks(all),
    unresolved: all.filter((s) => !isResolved(s)).length,
  };
}

/** 2.2.1: every number on the queue's stats strip, derived live. No counters.
 *
 *  Counts VIDEOS, because the queue's row is now a video. A video with no job
 *  is "not started" — it belongs in the corpus total and in nothing else. */
export function queueStats(rows: VideoRow[], ratePerMinute: number) {
  const started = rows.filter((r) => r.job_id !== null);
  const ready = rows.filter(
    (r) => r.current_stage === "AI" && r.current_status === "COMPLETED");
  // In progress = claimed and not yet handed over. COMPLETED at the ANNOTATOR
  // stage is the handoff to QA, not work in flight — the same distinction the
  // row pill makes.
  const inProgress = rows.filter(
    (r) => r.current_stage === "ANNOTATOR" && r.current_status !== "COMPLETED");
  // Submitted = waiting on QA, whoever holds it. A job sits at
  // ANNOTATOR/COMPLETED until QA claims it, so counting only stage === "QA"
  // showed zero for everything an annotator had actually submitted.
  const submitted = rows.filter(
    (r) => (r.current_stage === "ANNOTATOR" && r.current_status === "COMPLETED")
      || r.current_stage === "QA");
  const approvedVideos = rows.filter(
    (r) => r.current_status === "APPROVED" || r.current_stage === "DELIVERED");
  const rejectedVideos = rows.filter(
    (r) => r.current_stage === "QA" && r.current_status === "REJECTED");
  const approvedMinutes = approvedVideos
    .reduce((n, r) => n + (r.duration_seconds ?? 0), 0) / 60;
  return {
    total: rows.length,
    notStarted: rows.length - started.length,
    ready: ready.length,
    inProgress: inProgress.length,
    submitted: submitted.length,
    approvedVideos: approvedVideos.length,
    rejectedVideos: rejectedVideos.length,
    approvedMinutes,
    earned: approvedMinutes * ratePerMinute,
  };
}


/** Which page numbers to show, with nulls where the run is broken by a gap.
 *
 *  `[1, null, 4, 5, 6, null, 12]` renders as  1 … 4 5 6 … 12
 *
 *  WHY WINDOWED. Seventy videos is seven pages and every number fits. Seven
 *  hundred is seventy buttons, which wraps onto three rows and pushes the table
 *  down the screen — the control grows without bound while the thing it
 *  controls stays the same size.
 *
 *  First and last are always present, because "go to the end" is a real
 *  intention and scrolling a number line to reach it is not. The current page
 *  keeps `radius` neighbours either side so the next few steps are one click.
 *
 *  No gap is emitted for a single skipped page: "1 … 3" is wider than "1 2 3"
 *  and tells you less.
 */
export function pageNumbers(
  current: number,
  total: number,
  radius = 2,
): (number | null)[] {
  if (total <= 1) return [1];

  const wanted = new Set<number>([1, total]);
  for (let n = current - radius; n <= current + radius; n++) {
    if (n >= 1 && n <= total) wanted.add(n);
  }

  const sorted = [...wanted].sort((a, b) => a - b);
  const out: (number | null)[] = [];
  let previous = 0;
  for (const n of sorted) {
    // A gap of exactly one page is spelled out rather than elided.
    if (previous && n - previous > 1) {
      if (n - previous === 2) out.push(previous + 1);
      else out.push(null);
    }
    out.push(n);
    previous = n;
  }
  return out;
}
